class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // 使用事件委托，避免 fetch 重渲染导致遮罩元素替换后绑定丢失
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay' || event.target.closest('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    // Shopify adds wallet elements asynchronously and replaces the footer after cart updates.
    this.walletObserver = new MutationObserver(() => this.scheduleWalletSelection());
    this.walletObserver.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'disabled'] });
    this.scheduleWalletSelection();
  }

  disconnectedCallback() {
    this.walletObserver?.disconnect();
    cancelAnimationFrame(this.walletFrame);
  }

  scheduleWalletSelection() {
    if (this.walletFrame) return;
    this.walletFrame = requestAnimationFrame(() => {
      this.walletFrame = null;
      const checkout = this.querySelector('.ug-drawer__express shopify-accelerated-checkout-cart');
      const wallets = checkout ? Array.from(checkout.children).filter((element) =>
        /^(SHOPIFY-PAYPAL-BUTTON|SHOP-PAY-WALLET-BUTTON|SHOPIFY-APPLE-PAY-BUTTON|SHOPIFY-GOOGLE-PAY-BUTTON|SHOPIFY-AMAZON-PAY-BUTTON)$/.test(element.tagName)
      ) : [];
      const available = wallets.filter((button) => !button.hasAttribute('hidden') && !button.hasAttribute('disabled'));
      const selected = available.find((button) => button.tagName === 'SHOPIFY-PAYPAL-BUTTON') || available[0];
      wallets.forEach((button) => button.toggleAttribute('data-ug-wallet-selected', button === selected));
      this.querySelector('.ug-drawer__ctas')?.classList.toggle('has-express', Boolean(selected));
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if(!cartLink){
      return false
    }
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    
    if(window.Glood){
      (async ()=> {
        await Glood.upsell.refresh()
        await Glood.upsell.miniCart.hydrate({ 
          freeGiftSurface: 'cart',
          drawer: {
            selector: 'cart-drawer',   
            openClass: 'active',       
          },
        })
      })()
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // 这里不再单独给遮罩绑定 click，委托在 constructor 中已统一处理
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
  
  async refresh({ open = false } = {}) {
    const sectionIds = this.getSectionsToRender().map((s) => s.id);
    const response = await fetch(
      `${routes.cart_url}?sections=${sectionIds.join(',')}&sections_url=${encodeURIComponent(window.location.pathname)}`
    );
    const sections = await response.json();

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement || !sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });
    
    // Reset the container so that prices can be updated
    document
      .querySelectorAll('[data-glood-mini-cart-line]').forEach(line => line.dataset.gloodPriceUpdated = 'false')
    
      // emptiness is derived from the just-rendered DOM — Liquid omits
    // .drawer__inner-empty when cart has items, so checking .cart-item is enough.
    this.classList.toggle('is-empty', !this.querySelector('.cart-item'));

    // overlay element was just replaced — rebind always
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    if(window.Glood){
      await Glood.upsell.refresh()
    }
    if (open) {
      this.open(); // open() already calls Glood.upsell.miniCart.hydrate()
    } else {
      if(window.Glood){
        await Glood.upsell.miniCart.hydrate({ 
          freeGiftSurface: 'cart',
          drawer: {
            selector: 'cart-drawer',   
            openClass: 'active',       
          },
        })
      }
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
